import type { ChartPattern, PlanetPosition } from '@astrally/shared';
import { separation } from '../math/angles.js';
import { signName } from '../core/zodiac.js';

/**
 * Major chart-pattern detection on tropical positions:
 * stellium, grand trine, T-square, grand cross.
 */

const PATTERN_ORB = 8;

function isAspect(a: number, b: number, angle: number, orb = PATTERN_ORB): boolean {
  return Math.abs(separation(a, b) - angle) <= orb;
}

export function detectPatterns(positions: PlanetPosition[]): ChartPattern[] {
  const patterns: ChartPattern[] = [];
  const pts = positions.filter((p) => p.planet !== 'rahu' && p.planet !== 'ketu');

  // Stellium: 3+ planets in the same tropical sign.
  const bySign = new Map<number, PlanetPosition[]>();
  for (const p of pts) {
    const list = bySign.get(p.tropical.sign) ?? [];
    list.push(p);
    bySign.set(p.tropical.sign, list);
  }
  for (const [sign, group] of bySign) {
    if (group.length >= 3) {
      patterns.push({
        type: 'stellium',
        points: group.map((p) => p.planet),
        detail: `${group.length} planets in ${signName(sign)}`,
      });
    }
  }

  // Grand trine: three planets pairwise trine.
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      for (let k = j + 1; k < pts.length; k++) {
        const [a, b, c] = [pts[i], pts[j], pts[k]];
        if (
          isAspect(a.lonTropical, b.lonTropical, 120) &&
          isAspect(b.lonTropical, c.lonTropical, 120) &&
          isAspect(a.lonTropical, c.lonTropical, 120)
        ) {
          patterns.push({
            type: 'grand-trine',
            points: [a.planet, b.planet, c.planet],
            detail: 'Three planets in mutual trine',
          });
        }
      }
    }
  }

  // T-square: opposition with both ends square a third planet.
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      if (!isAspect(pts[i].lonTropical, pts[j].lonTropical, 180)) continue;
      for (let k = 0; k < pts.length; k++) {
        if (k === i || k === j) continue;
        if (
          isAspect(pts[i].lonTropical, pts[k].lonTropical, 90) &&
          isAspect(pts[j].lonTropical, pts[k].lonTropical, 90)
        ) {
          patterns.push({
            type: 't-square',
            points: [pts[i].planet, pts[j].planet, pts[k].planet],
            detail: `${pts[k].planet} at the apex of an opposition`,
          });
        }
      }
    }
  }

  // Grand cross: two oppositions all in mutual square.
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      if (!isAspect(pts[i].lonTropical, pts[j].lonTropical, 180)) continue;
      for (let k = i + 1; k < pts.length; k++) {
        for (let l = k + 1; l < pts.length; l++) {
          if (k === j || l === j) continue;
          if (!isAspect(pts[k].lonTropical, pts[l].lonTropical, 180)) continue;
          if (
            isAspect(pts[i].lonTropical, pts[k].lonTropical, 90) &&
            isAspect(pts[j].lonTropical, pts[k].lonTropical, 90) &&
            isAspect(pts[i].lonTropical, pts[l].lonTropical, 90) &&
            isAspect(pts[j].lonTropical, pts[l].lonTropical, 90)
          ) {
            patterns.push({
              type: 'grand-cross',
              points: [pts[i].planet, pts[j].planet, pts[k].planet, pts[l].planet],
              detail: 'Two oppositions locked in mutual square',
            });
          }
        }
      }
    }
  }

  return dedupePatterns(patterns);
}

function dedupePatterns(patterns: ChartPattern[]): ChartPattern[] {
  const seen = new Set<string>();
  return patterns.filter((p) => {
    const key = p.type + ':' + [...p.points].sort().join(',');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
